/* eslint-disable react/prop-types */
import classNames from 'classnames';
import React from 'react';
import { Form } from 'react-bootstrap';
import {
  useTable,
  useSortBy,
  usePagination,
  useRowSelect,
  useGlobalFilter
} from 'react-table';
import {
  getStoredTablePageSize,
  setStoredTablePageSize
} from 'components/common/table/tablePageSizeStorage';

export const IndeterminateCheckbox = React.forwardRef(
  ({ indeterminate, className, ...rest }, ref) => {
    const defaultRef = React.useRef();

    const resolvedRef = ref || defaultRef;

    React.useEffect(() => {
      resolvedRef.current.indeterminate = indeterminate;
    }, [resolvedRef, indeterminate]);

    return (
      <Form.Check
        type="checkbox"
        className={classNames('form-check fs-0 mb-0', className)}
      >
        <Form.Check.Input type="checkbox" ref={resolvedRef} {...rest} />
      </Form.Check>
    );
  }
);

const AdvanceTableWrapper = ({
  children,
  columns,
  data,
  sortable,
  selection,
  selectionColumnWidth,
  pagination,
  perPage = 10,
  pageSizeStorageKey,
  sortBy
}) => {
  const initialPageSize = React.useMemo(
    () => getStoredTablePageSize(perPage, pageSizeStorageKey),
    [pageSizeStorageKey, perPage]
  );
  const {
    getTableProps,
    headers,
    page,
    rows,
    prepareRow,
    canPreviousPage,
    canNextPage,
    nextPage,
    previousPage,
    setPageSize: setTablePageSize,
    gotoPage,
    pageCount,
    state: { pageIndex, pageSize, selectedRowIds, globalFilter },
    setGlobalFilter
  } = useTable(
    {
      columns,
      data,
      disableSortBy: !sortable,
      initialState: {
        pageSize: pagination ? initialPageSize : data.length,
        sortBy: sortBy || [
          {
            id: 'title',
            desc: true
          }
        ]
      }
    },
    useGlobalFilter,
    useSortBy,
    usePagination,
    useRowSelect,
    hooks => {
      if (selection) {
        hooks.visibleColumns.push(columns => [
          {
            id: 'selection',
            Header: ({ getToggleAllRowsSelectedProps }) => (
              <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
            ),
            headerProps: {
              style: {
                width: selectionColumnWidth
              }
            },
            cellProps: {
              style: {
                width: selectionColumnWidth
              }
            },
            Cell: ({ row }) => (
              <div>
                <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
              </div>
            )
          },
          ...columns
        ]);
      }
    }
  );

  // Track previous applied page size to avoid resetting the table on each render.
  const prevPageSizeRef = React.useRef(initialPageSize);

  const setPageSize = React.useCallback(
    nextPageSize => {
      const safePageSize = Number(nextPageSize);

      if (Number.isFinite(safePageSize) && safePageSize > 0) {
        setStoredTablePageSize(safePageSize, perPage, pageSizeStorageKey);
        prevPageSizeRef.current = safePageSize;
        setTablePageSize(safePageSize);
      }
    },
    [pageSizeStorageKey, perPage, setTablePageSize]
  );

  // Update pageSize when the persisted key or default changes.
  React.useEffect(() => {
    if (pagination && initialPageSize !== prevPageSizeRef.current) {
      prevPageSizeRef.current = initialPageSize;
      setTablePageSize(initialPageSize);
    }
  }, [initialPageSize, pagination, setTablePageSize]);

  const recursiveMap = children => {
    return React.Children.map(children, child => {
      if (child?.props?.children) {
        return React.cloneElement(child, {
          children: recursiveMap(child.props.children)
        });
      } else {
        if (child?.props?.table) {
          return React.cloneElement(child, {
            ...child.props,
            getTableProps,
            headers,
            page,
            prepareRow,
            canPreviousPage,
            canNextPage,
            nextPage,
            previousPage,
            gotoPage,
            pageCount,
            pageIndex,
            selectedRowIds,
            pageSize,
            rowCount: rows.length,
            setPageSize,
            globalFilter,
            setGlobalFilter
          });
        } else {
          return child;
        }
      }
    });
  };

  return (
    // <>
    //   {React.Children.map(children, child => {
    //     if (child.props.table) {
    //       return React.cloneElement(child, {
    //         ...child.props,
    //         getTableProps,
    //         headers,
    //         page,
    //         prepareRow,
    //         canPreviousPage,
    //         canNextPage,
    //         nextPage,
    //         previousPage,
    //         gotoPage,
    //         pageCount,
    //         pageIndex,
    //         selectedRowIds,
    //         pageSize,
    //         setPageSize
    //       });
    //     } else {
    //       return child;
    //     }
    //   })}
    // </>
    <>{recursiveMap(children)}</>
  );
};

export default AdvanceTableWrapper;
